import { Injectable } from '@angular/core';
import { GoogleGenAI, Type } from '@google/genai';
import { McpService } from './mcp.service';

@Injectable({
  providedIn: 'root'
})
export class ApiDocsAgentService {
  private ai: GoogleGenAI;
  private readonly modelName = 'gemini-flash-latest';

  constructor(private mcpService: McpService) {
    this.ai = new GoogleGenAI({
      apiKey: 'AIzaSyCEQ-DkCwkA3ksRpzaNsgB8m1eEfkLADhA'
    });
  }

  async answerWithTools(
    systemInstruction: string,
    userPrompt: string
  ): Promise<string> {
    let mcpTools: any[] = [];
    try {
      const toolsResponse = await this.mcpService.getTools();
      if (toolsResponse && toolsResponse.tools) {
        mcpTools = toolsResponse.tools;
      }
    } catch (err) {
      console.error('Failed to get MCP tools:', err);
      // Fallback: Continue without tools or handle the error
    }

    // Map MCP tools to Gemini tools format
    const geminiTools = mcpTools.map(tool => {
      return {
        name: tool.name.replace(/-/g, '_'),
        description: tool.description,
        parameters: this.convertSchema(tool.inputSchema)
      };
    });

    const config: any = {
      systemInstruction: [{ text: systemInstruction }],
      temperature: 0.1,
    };

    if (geminiTools.length > 0) {
      config.tools = [{ functionDeclarations: geminiTools }];
    }

    const chat = this.ai.chats.create({
      model: this.modelName,
      config
    });

    try {
      let currentResponse = await chat.sendMessage({ message: userPrompt });

      // Handle function calls loop
      let iterations = 0;
      while (currentResponse.functionCalls && currentResponse.functionCalls.length > 0 && iterations < 10) {
        iterations++;
        const toolResponses: any[] = [];

        for (const call of currentResponse.functionCalls) {
          try {
            const mcpToolName = call.name.replace(/_/g, '-');
            const result = await this.mcpService.callTool(mcpToolName, call.args);
            
            let resultData: any = result;
            if (result && result.content && Array.isArray(result.content)) {
              resultData = result.content.map((c: any) => c.text).join('\n');
            }

            let responsePayload: any = { result: resultData };
            
            // Try to parse stringified JSON from MCP so Gemini gets a real object instead of an escaped string
            if (typeof resultData === 'string') {
              try {
                const parsed = JSON.parse(resultData);
                // If it's already an object, use it directly as the response
                if (parsed && typeof parsed === 'object') {
                  responsePayload = parsed;
                }
              } catch(e) {
                // Ignore parsing error, fallback to { result: string }
              }
            } else if (resultData && typeof resultData === 'object') {
              responsePayload = resultData;
            }

            toolResponses.push({
              functionResponse: {
                id: (call as any).id, // Gemini SDK might use id
                name: call.name,
                response: responsePayload
              }
            });
          } catch (err: any) {
            console.error(`Tool execution error for ${call.name}:`, err);
            toolResponses.push({
              functionResponse: {
                id: (call as any).id,
                name: call.name,
                response: { error: err.message || err.toString() }
              }
            });
          }
        }

        currentResponse = await chat.sendMessage({ message: toolResponses });
      }

      return currentResponse.text || '';
    } catch (err) {
      console.error('Error in chatWithTools:', err);
      return 'Error: Could not complete the request to the API Documentation agent.';
    }
  }

  private convertSchema(schema: any): any {
    if (!schema || typeof schema !== 'object') return schema;
    
    const converted: any = { ...schema };
    if (converted.$schema) delete converted.$schema;
    if (converted.additionalProperties !== undefined) delete converted.additionalProperties;

    if (converted.type && typeof converted.type === 'string') {
      converted.type = converted.type.toUpperCase();
    }

    if (converted.properties) {
      for (const key in converted.properties) {
        converted.properties[key] = this.convertSchema(converted.properties[key]);
      }
    }
    
    if (converted.items) {
      converted.items = this.convertSchema(converted.items);
    }

    return converted;
  }
}